import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, map, Observable, of, tap} from 'rxjs';
import {FormDefinition} from '../models/form-schema.model';
import {v4 as uuidv4} from 'uuid';
import {CookieService} from "../../../../_services/cookie.service";
import {CookieType} from "../../../../x-models/x-enums/cookie-type";
import {FormHttpService} from "../../data-access/form-http.service";
import {environment} from "../../../../../environments/environment";

/**
 * Form persistence facade. Backed by the Forms microservice when
 * `environment.formsBackendEnabled` is set, otherwise by localStorage so the
 * builder is fully usable without the backend stack running.
 */
@Injectable({
  providedIn: 'root'
})
export class FormService {
  private readonly useBackend = environment.formsBackendEnabled === true;
  private forms = new BehaviorSubject<FormDefinition[]>([]);
  forms$ = this.forms.asObservable();

  private readonly STORAGE_KEY = 'cheqme_forms';

  constructor(private cookieService: CookieService, private http: FormHttpService) {
    if (this.useBackend) {
      this.refreshFromBackend();
    } else {
      this.forms.next(this.loadFromStorage());
    }
  }

  getForms(): Observable<FormDefinition[]> {
    if (this.useBackend) {
      this.refreshFromBackend();
    }
    return this.forms$;
  }

  getForm(id: string): Observable<FormDefinition | undefined> {
    if (this.useBackend) {
      return this.http.getDefinition(id).pipe(catchError(() => of(undefined)));
    }
    return of(this.forms.value.find(f => f.id === id));
  }

  addForm(title: string): Observable<FormDefinition> {
    const newForm: FormDefinition = {
      id: uuidv4(),
      key: title.toLowerCase().replace(/\s+/g, '-'),
      title: title,
      version: 1,
      status: 'draft',
      pages: [{id: uuidv4(), title: 'Seite 1', blocks: []}],
      validations: []
    };

    if (this.useBackend) {
      return this.http.upsertDefinition(newForm).pipe(tap(() => this.refreshFromBackend()));
    }

    this.saveToStorage([...this.forms.value, newForm]);
    return of(newForm);
  }

  updateForm(form: FormDefinition): Observable<FormDefinition> {
    if (this.useBackend) {
      return this.http.upsertDefinition(form).pipe(tap(() => this.refreshFromBackend()));
    }

    const current = this.forms.value;
    const index = current.findIndex(f => f.id === form.id);
    if (index !== -1) {
      current[index] = form;
      this.saveToStorage([...current]);
    } else {
      this.saveToStorage([...current, form]);
    }
    return of(form);
  }

  deleteForm(id: string): Observable<boolean> {
    if (this.useBackend) {
      return this.http.deleteDefinition(id).pipe(
        tap(() => this.refreshFromBackend()),
        map(() => true),
        catchError(() => of(false))
      );
    }

    const filtered = this.forms.value.filter(f => f.id !== id);
    this.saveToStorage(filtered);
    return of(true);
  }

  // --- backend ---------------------------------------------------------------
  private refreshFromBackend() {
    this.http.listDefinitions()
      .pipe(catchError(() => of([] as FormDefinition[])))
      .subscribe(forms => this.forms.next(forms));
  }

  // --- localStorage ----------------------------------------------------------
  private loadFromStorage(): FormDefinition[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Error parsing stored forms', e);
        return [];
      }
    }
    return [];
  }

  private saveToStorage(forms: FormDefinition[]) {
    this.cookieService.setLocalStorage(CookieType.Required, this.STORAGE_KEY, JSON.stringify(forms));
    this.forms.next(forms);
  }
}
